import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff, Upload, Settings } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export default function AdManager() {
  const [ads, setAds] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAdsenseModal, setShowAdsenseModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [adsenseConfig, setAdsenseConfig] = useState({ head_script: '', is_enabled: false });
  const [uploading, setUploading] = useState(false);
  
  const audioInputRef = useRef(null);
  const imageInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'visual',
    title: '',
    description: '',
    click_url: '',
    target_countries: '',
    target_languages: '',
    start_date: '',
    end_date: '',
    frequency_tracks: 3,
    position: 'banner_bottom',
    priority: 0,
    is_adsense: false,
    adsense_slot: '',
    adsense_script: ''
  });
  
  const [selectedAudioFile, setSelectedAudioFile] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);

  useEffect(() => {
    loadAds();
    loadStats();
    loadAdsenseConfig();
  }, []);

  const loadAds = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/ads/admin/list`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setAds(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement publicités:', error);
      setAds([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ads/admin/stats`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  const loadAdsenseConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ads/admin/adsense`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setAdsenseConfig(data);
    } catch (error) {
      console.error('Erreur chargement config AdSense:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      const formDataToSend = new FormData();
      
      // Ajouter les champs texte
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Ajouter les fichiers
      if (formData.type === 'audio' && selectedAudioFile) {
        formDataToSend.append('audio_file', selectedAudioFile);
      }
      if (formData.type === 'visual' && selectedImageFile && !formData.is_adsense) {
        formDataToSend.append('image_file', selectedImageFile);
      }
      
      // Utilise POST uniquement (compatibilité OVH mutualisé)
      const url = editingAd 
        ? `${API_URL}/api/ads/admin/${editingAd.id}/update`
        : `${API_URL}/api/ads/admin/create`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formDataToSend
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erreur serveur');
      }
      
      setShowModal(false);
      setEditingAd(null);
      resetForm();
      loadAds();
      loadStats();
    } catch (error) {
      console.error('Erreur sauvegarde pub:', error);
      alert('Erreur: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (ad) => {
    setEditingAd(ad);
    setFormData({
      name: ad.name || '',
      type: ad.type || 'visual',
      title: ad.title || '',
      description: ad.description || '',
      click_url: ad.click_url || '',
      target_countries: ad.target_countries || '',
      target_languages: ad.target_languages || '',
      start_date: ad.start_date ? ad.start_date.split('T')[0] : '',
      end_date: ad.end_date ? ad.end_date.split('T')[0] : '',
      frequency_tracks: ad.frequency_tracks || 3,
      position: ad.position || 'banner_bottom',
      priority: ad.priority || 0,
      is_adsense: !!ad.is_adsense,
      adsense_slot: ad.adsense_slot || '',
      adsense_script: ad.adsense_script || ''
    });
    setSelectedAudioFile(null);
    setSelectedImageFile(null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette publicité ?')) return;
    try {
      // Utilise POST au lieu de DELETE (compatibilité OVH mutualisé)
      await fetch(`${API_URL}/api/ads/admin/${id}/delete`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      loadAds();
      loadStats();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleToggle = async (id) => {
    try {
      // Utilise POST au lieu de PATCH (compatibilité OVH mutualisé)
      await fetch(`${API_URL}/api/ads/admin/${id}/toggle`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      loadAds();
    } catch (error) {
      console.error('Erreur toggle:', error);
    }
  };

  const handleSaveAdsense = async () => {
    try {
      // Utilise POST au lieu de PUT (compatibilité OVH mutualisé)
      await fetch(`${API_URL}/api/ads/admin/adsense/update`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(adsenseConfig)
      });
      setShowAdsenseModal(false);
      alert('Configuration AdSense sauvegardée !');
    } catch (error) {
      console.error('Erreur sauvegarde AdSense:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'visual',
      title: '',
      description: '',
      click_url: '',
      target_countries: '',
      target_languages: '',
      start_date: '',
      end_date: '',
      frequency_tracks: 3,
      position: 'banner_bottom',
      priority: 0,
      is_adsense: false,
      adsense_slot: '',
      adsense_script: ''
    });
    setSelectedAudioFile(null);
    setSelectedImageFile(null);
  };

  const typeLabels = {
    'visual': '🖼️ Visuelle',
    'audio': '🔊 Audio'
  };

  const positionLabels = {
    'banner_top': '📍 Bannière haut de page',
    'banner_bottom': '📍 Bannière bas de page',
    'card': '🃏 Carte (entre les contenus)',
    'sidebar_left': '◀️ Barre latérale gauche',
    'sidebar_right': '▶️ Barre latérale droite',
    'page_banner': '📄 Bannière de page (Artiste/Playlist)',
    'interstitial': '🔲 Popup interstitiel (plein écran)',
    'popup': '💬 Popup',
    'sidebar': '📌 Barre latérale (ancien)'
  };

  return (
    <div className="space-y-6">
      {/* Config AdSense Global */}
      <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-lg p-4 border border-blue-500/30">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold flex items-center gap-2">
              <Settings size={18} />
              Google AdSense
            </h3>
            <p className="text-sm text-white/60 mt-1">
              {adsenseConfig.is_enabled ? '✅ Activé' : '❌ Désactivé'} - Configurez le script global AdSense
            </p>
          </div>
          <button
            onClick={() => setShowAdsenseModal(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-400 flex items-center gap-2"
          >
            <Settings size={16} />
            Configurer AdSense
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/5 rounded-lg p-4">
            <p className="text-white/60 text-sm">Total Publicités</p>
            <p className="text-2xl font-bold">{stats.summary?.total_ads || 0}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4">
            <p className="text-white/60 text-sm">Actives</p>
            <p className="text-2xl font-bold text-green-500">{stats.summary?.active_ads || 0}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4">
            <p className="text-white/60 text-sm">Impressions</p>
            <p className="text-2xl font-bold">{stats.summary?.total_impressions || 0}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-4">
            <p className="text-white/60 text-sm">Clics (CTR)</p>
            <p className="text-2xl font-bold">{stats.summary?.total_clicks || 0} <span className="text-sm text-white/60">({stats.summary?.avg_ctr || 0}%)</span></p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Gestion des Publicités</h2>
        <button
          onClick={() => {
            resetForm();
            setEditingAd(null);
            setShowModal(true);
          }}
          className="flex items-center gap-2 bg-green-500 text-black px-4 py-2 rounded-lg hover:bg-green-400"
        >
          <Plus size={18} />
          Nouvelle Publicité
        </button>
      </div>

      {/* Ads List */}
      {loading ? (
        <div className="text-center py-12 text-white/60">Chargement...</div>
      ) : ads.length === 0 ? (
        <div className="text-center py-12 text-white/60">
          Aucune publicité. Créez votre première publicité !
        </div>
      ) : (
        <div className="grid gap-4">
          {ads.map((ad) => (
            <div
              key={`${ad.id}-${ad.updated_at || ad.id}`}
              className={`bg-white/5 rounded-lg p-4 ${!ad.is_active ? 'opacity-50' : ''}`}
            >
              <div className="flex items-start gap-4">
                {/* Preview - Ajout d'un cache-buster basé sur updated_at */}
                {ad.type === 'visual' && ad.image_url && (
                  <img
                    src={`${ad.image_url}${ad.image_url.includes('?') ? '&' : '?'}t=${new Date(ad.updated_at || Date.now()).getTime()}`}
                    alt={ad.name}
                    className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                    loading="lazy"
                  />
                )}
                {ad.type === 'visual' && !ad.image_url && !Number(ad.is_adsense) && (
                  <div className="w-20 h-20 bg-gradient-to-br from-gray-600 to-gray-800 rounded-lg flex items-center justify-center flex-shrink-0 border border-dashed border-white/20">
                    <span className="text-2xl">🖼️</span>
                  </div>
                )}
                {ad.type === 'visual' && !!Number(ad.is_adsense) && (
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-bold text-white">Ad</span>
                  </div>
                )}
                {ad.type === 'audio' && (
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-3xl">🔊</span>
                  </div>
                )}
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-semibold truncate">{ad.name}</h3>
                    <span className="text-xs px-2 py-0.5 bg-white/10 rounded">{typeLabels[ad.type]}</span>
                    {ad.type === 'visual' && <span className="text-xs px-2 py-0.5 bg-white/10 rounded">{positionLabels[ad.position]}</span>}
                    {!!Number(ad.is_adsense) && <span className="text-xs px-2 py-0.5 bg-blue-500/30 text-blue-300 rounded">AdSense</span>}
                  </div>
                  
                  {ad.title && <p className="text-white/80 text-sm">{ad.title}</p>}
                  {ad.description && <p className="text-white/60 text-sm truncate">{ad.description}</p>}
                  
                  <div className="flex flex-wrap gap-2 mt-2 text-xs text-white/60">
                    {ad.target_countries && <span>🌍 {ad.target_countries}</span>}
                    {ad.target_languages && <span>🗣️ {ad.target_languages}</span>}
                    {ad.type === 'audio' && <span>🎵 Toutes les {ad.frequency_tracks} pistes</span>}
                    <span>📊 {ad.impressions || 0} vues / {ad.clicks || 0} clics</span>
                  </div>
                  
                  {(ad.start_date || ad.end_date) && (
                    <p className="text-xs text-white/40 mt-1">
                      📅 {ad.start_date ? new Date(ad.start_date).toLocaleDateString() : '∞'} 
                      → {ad.end_date ? new Date(ad.end_date).toLocaleDateString() : '∞'}
                    </p>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleToggle(ad.id)}
                    className={`p-2 rounded-lg transition-colors ${ad.is_active ? 'bg-green-500/20 text-green-500' : 'bg-white/10 text-white/60'}`}
                    title={ad.is_active ? 'Désactiver' : 'Activer'}
                  >
                    {ad.is_active ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                  <button
                    onClick={() => handleEdit(ad)}
                    className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                    title="Modifier"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(ad.id)}
                    className="p-2 rounded-lg bg-red-500/20 text-red-500 hover:bg-red-500/30 transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Création/Modification */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 modal-text">
          <div className="bg-[#282828] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-xl font-bold">
                {editingAd ? 'Modifier la publicité' : 'Nouvelle publicité'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Nom et Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white/60 mb-1">Nom interne *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-white/10 rounded-lg px-3 py-2 text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/60 mb-1">Type *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value, is_adsense: false })}
                    className="w-full bg-white/10 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="visual">🖼️ Visuelle (Bannière/Image)</option>
                    <option value="audio">🔊 Audio</option>
                  </select>
                </div>
              </div>

              {/* Contenu selon le type */}
              {formData.type === 'audio' && (
                <div className="bg-white/5 rounded-lg p-4">
                  <label className="block text-sm text-white/60 mb-2">Fichier Audio (MP3) *</label>
                  <input
                    ref={audioInputRef}
                    type="file"
                    accept="audio/mp3,audio/mpeg"
                    onChange={(e) => setSelectedAudioFile(e.target.files[0])}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => audioInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-3 bg-white/10 rounded-lg hover:bg-white/20 w-full justify-center"
                  >
                    <Upload size={18} />
                    {selectedAudioFile ? selectedAudioFile.name : (editingAd?.audio_url ? '📁 Fichier existant - Cliquez pour remplacer' : 'Choisir un fichier audio')}
                  </button>
                  {editingAd?.audio_url && !selectedAudioFile && (
                    <p className="text-xs text-white/40 mt-2">Audio actuel: {editingAd.audio_url.split('/').pop()}</p>
                  )}
                </div>
              )}

              {formData.type === 'visual' && (
                <>
                  {/* Option AdSense */}
                  <div className="flex items-center gap-3 p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <input
                      type="checkbox"
                      id="is_adsense"
                      checked={formData.is_adsense}
                      onChange={(e) => setFormData({ ...formData, is_adsense: e.target.checked })}
                      className="w-5 h-5"
                    />
                    <label htmlFor="is_adsense" className="text-sm">
                      Utiliser Google AdSense au lieu d'une image personnalisée
                    </label>
                  </div>

                  {formData.is_adsense ? (
                    <div className="bg-white/5 rounded-lg p-4">
                      <label className="block text-sm text-white/60 mb-2">Script de la publicité AdSense *</label>
                      <textarea
                        value={formData.adsense_script}
                        onChange={(e) => setFormData({ ...formData, adsense_script: e.target.value })}
                        className="w-full bg-white/10 rounded-lg px-3 py-2 text-white font-mono text-xs"
                        rows={6}
                        placeholder='<ins class="adsbygoogle"
     style="display:block"
     data-ad-client="ca-pub-XXXXXXXXXX"
     data-ad-slot="1234567890"
     data-ad-format="auto"></ins>
<script>(adsbygoogle = window.adsbygoogle || []).push({});</script>'
                      />
                      <p className="text-xs text-white/40 mt-2">
                        Collez ici le code complet de l'unité publicitaire AdSense (le bloc &lt;ins&gt; + &lt;script&gt;)
                      </p>
                    </div>
                  ) : (
                    <div className="bg-white/5 rounded-lg p-4">
                      <label className="block text-sm text-white/60 mb-2">Image de la publicité *</label>
                      <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => setSelectedImageFile(e.target.files[0])}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => imageInputRef.current?.click()}
                        className="flex items-center gap-2 px-4 py-3 bg-white/10 rounded-lg hover:bg-white/20 w-full justify-center"
                      >
                        <Upload size={18} />
                        {selectedImageFile ? selectedImageFile.name : (editingAd?.image_url ? '📁 Image existante - Cliquez pour remplacer' : 'Choisir une image')}
                      </button>
                      {editingAd?.image_url && !selectedImageFile && (
                        <div className="mt-2">
                          <img 
                            src={`${editingAd.image_url}${editingAd.image_url.includes('?') ? '&' : '?'}t=${new Date(editingAd.updated_at || Date.now()).getTime()}`} 
                            alt="Preview" 
                            className="w-32 h-20 object-cover rounded" 
                          />
                        </div>
                      )}
                      {selectedImageFile && (
                        <div className="mt-2">
                          <img 
                            src={URL.createObjectURL(selectedImageFile)} 
                            alt="Nouvelle image" 
                            className="w-32 h-20 object-cover rounded border-2 border-green-500" 
                          />
                          <p className="text-xs text-green-400 mt-1">Nouvelle image sélectionnée</p>
                        </div>
                      )}
                      <p className="text-xs text-white/40 mt-2">L'image sera automatiquement compressée</p>
                    </div>
                  )}
                </>
              )}

              {/* Titre et Description - Masqués pour AdSense */}
              {!formData.is_adsense && (
                <>
                  <div>
                    <label className="block text-sm text-white/60 mb-1">Titre affiché</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full bg-white/10 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-1">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full bg-white/10 rounded-lg px-3 py-2 text-white"
                      rows={2}
                    />
                  </div>

                  {/* URL de clic */}
                  <div>
                    <label className="block text-sm text-white/60 mb-1">URL de redirection (clic)</label>
                    <input
                      type="url"
                      value={formData.click_url}
                      onChange={(e) => setFormData({ ...formData, click_url: e.target.value })}
                      className="w-full bg-white/10 rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="https://..."
                    />
                  </div>

                  {/* Ciblage géographique */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-white/60 mb-1">Pays cibles (codes)</label>
                      <input
                        type="text"
                        value={formData.target_countries}
                        onChange={(e) => setFormData({ ...formData, target_countries: e.target.value })}
                        className="w-full bg-white/10 rounded-lg px-3 py-2 text-white"
                        placeholder="FR,BE,CA,CH (vide = tous)"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-white/60 mb-1">Langues cibles</label>
                      <input
                        type="text"
                        value={formData.target_languages}
                        onChange={(e) => setFormData({ ...formData, target_languages: e.target.value })}
                        className="w-full bg-white/10 rounded-lg px-3 py-2 text-white"
                        placeholder="fr,en (vide = toutes)"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white/60 mb-1">Date de début</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full bg-white/10 rounded-lg px-3 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/60 mb-1">Date de fin</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full bg-white/10 rounded-lg px-3 py-2 text-white"
                  />
                </div>
              </div>

              {/* Options */}
              <div className="grid grid-cols-3 gap-4">
                {formData.type === 'visual' && (
                  <div className="col-span-2">
                    <label className="block text-sm text-white/60 mb-1">Position / Emplacement *</label>
                    <select
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      className="w-full bg-white/10 rounded-lg px-3 py-2 text-white"
                    >
                      <optgroup label="Bannières">
                        <option value="banner_top">📍 Bannière haut de page</option>
                        <option value="banner_bottom">📍 Bannière bas de page</option>
                      </optgroup>
                      <optgroup label="Contenu">
                        <option value="card">🃏 Carte (entre les contenus des sections)</option>
                        <option value="page_banner">📄 Bannière de page (Artiste/Playlist/Likés)</option>
                      </optgroup>
                      <optgroup label="Barres latérales">
                        <option value="sidebar_left">◀️ Barre latérale gauche</option>
                        <option value="sidebar_right">▶️ Barre latérale droite</option>
                      </optgroup>
                      <optgroup label="Popups">
                        <option value="interstitial">🔲 Popup interstitiel (plein écran avant navigation)</option>
                      </optgroup>
                    </select>
                    <p className="text-xs text-white/40 mt-1">
                      {formData.position === 'card' && "S'affiche entre les cartes des sections (Tendance, Nouveautés, etc.)"}
                      {formData.position === 'interstitial' && "S'affiche en plein écran avant d'accéder à une page"}
                      {formData.position === 'page_banner' && "S'affiche sur les pages Artiste, Playlist, Titres likés"}
                    </p>
                  </div>
                )}
                {formData.type === 'audio' && (
                  <div>
                    <label className="block text-sm text-white/60 mb-1">Fréquence (pistes)</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.frequency_tracks}
                      onChange={(e) => setFormData({ ...formData, frequency_tracks: parseInt(e.target.value) || 3 })}
                      className="w-full bg-white/10 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm text-white/60 mb-1">Priorité</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                    className="w-full bg-white/10 rounded-lg px-3 py-2 text-white"
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingAd(null);
                  }}
                  className="flex-1 py-2 rounded-lg border border-white/20 hover:bg-white/10"
                  disabled={uploading}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 rounded-lg bg-green-500 text-black font-medium hover:bg-green-400 disabled:opacity-50"
                  disabled={uploading}
                >
                  {uploading ? 'Envoi en cours...' : (editingAd ? 'Mettre à jour' : 'Créer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal AdSense Config */}
      {showAdsenseModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 modal-text">
          <div className="bg-[#282828] rounded-xl max-w-2xl w-full">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-xl font-bold">Configuration Google AdSense</h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                <input
                  type="checkbox"
                  id="adsense_enabled"
                  checked={adsenseConfig.is_enabled}
                  onChange={(e) => setAdsenseConfig({ ...adsenseConfig, is_enabled: e.target.checked })}
                  className="w-5 h-5"
                />
                <label htmlFor="adsense_enabled" className="font-medium">
                  Activer Google AdSense sur le site
                </label>
              </div>

              <div>
                <label className="block text-sm text-white/60 mb-2">
                  Script AdSense (à insérer dans le &lt;head&gt;)
                </label>
                <textarea
                  value={adsenseConfig.head_script}
                  onChange={(e) => setAdsenseConfig({ ...adsenseConfig, head_script: e.target.value })}
                  className="w-full bg-white/10 rounded-lg px-3 py-2 text-white font-mono text-sm"
                  rows={6}
                  placeholder='<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-XXXXXXXXXX" crossorigin="anonymous"></script>'
                />
                <p className="text-xs text-white/40 mt-2">
                  Collez ici le script fourni par Google AdSense. Il sera automatiquement injecté dans le &lt;head&gt; du site.
                </p>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <h4 className="font-medium text-yellow-400 mb-2">💡 Comment utiliser AdSense</h4>
                <ol className="text-sm text-white/70 space-y-1 list-decimal list-inside">
                  <li>Collez votre script AdSense global ci-dessus</li>
                  <li>Activez AdSense avec la case à cocher</li>
                  <li>Créez une publicité visuelle et cochez "Utiliser Google AdSense"</li>
                  <li>Entrez l'ID du slot AdSense pour cet emplacement</li>
                </ol>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAdsenseModal(false)}
                  className="flex-1 py-2 rounded-lg border border-white/20 hover:bg-white/10"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveAdsense}
                  className="flex-1 py-2 rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-400"
                >
                  Sauvegarder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
